import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos, categories } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === Number(id));

  const categoryName = useMemo(() => {
    if (!video) return '';
    const cat = categories.find((c) => c.slug === video.category);
    return cat?.name || '';
  }, [video]);

  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.category === video.category && v.id !== video.id)
      .slice(0, 6);
  }, [video]);

  useEffect(() => {
    if (video) {
      document.title = `${video.title} - 麻花视频在线观看${video.title}`;
    } else {
      document.title = '内容未找到 - 麻花视频';
    }
  }, [video]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">内容未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该视频可能已被下架或链接错误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article className="container-page">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: categoryName, to: `/category/${video.category}` },
              { label: video.title },
            ]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Video Player Area */}
            <div className="lg:col-span-2">
              <div className="relative aspect-video rounded-xl overflow-hidden bg-background-900 mb-6">
                <img
                  src={video.coverImage}
                  alt={video.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-600 cursor-pointer transition-all duration-300 hover:scale-110">
                    <i className="ri-play-fill text-3xl md:text-4xl text-background-50 ml-1"></i>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 text-white text-sm">
                  <span>{video.episodes}</span>
                </div>
              </div>

              {/* Video Info */}
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                {video.title}
              </h1>

              <div className="flex items-center gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-1 text-accent-500">
                  <i className="ri-star-fill"></i>
                  <span className="text-sm font-semibold">{video.rating}</span>
                </div>
                <span className="text-foreground-400 text-sm">|</span>
                <span className="text-sm text-foreground-500">{video.year}</span>
                <span className="text-sm text-foreground-500">{video.region}</span>
                <span className="text-sm text-foreground-500">{video.subcategory}</span>
                {video.director && (
                  <>
                    <span className="text-foreground-400 text-sm">|</span>
                    <span className="text-sm text-foreground-500">导演：{video.director}</span>
                  </>
                )}
              </div>

              {video.tags.length > 0 && (
                <div className="flex items-center gap-2 mb-5 flex-wrap">
                  {video.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="prose prose-sm max-w-none mb-6">
                <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">剧情简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
              </div>

              {video.actors.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">主演</h3>
                  <div className="flex items-center gap-3 flex-wrap">
                    {video.actors.map((actor) => (
                      <span key={actor} className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-700 text-sm whitespace-nowrap">
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 text-xs text-foreground-400 mb-10">
                <span><i className="ri-eye-line mr-1"></i>{(video.views / 10000).toFixed(0)}万次播放</span>
              </div>
            </div>

            {/* Right: Related Videos */}
            <div className="lg:col-span-1">
              <div className="sticky top-20">
                <h3 className="text-base font-heading font-semibold text-foreground-800 mb-4">
                  相关推荐
                </h3>
                {relatedVideos.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {relatedVideos.map((rv) => (
                      <Link
                        key={rv.id}
                        to={`/article/${rv.id}`}
                        className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer group"
                      >
                        <div className="w-28 h-20 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={rv.coverImage}
                            alt={rv.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                            {rv.title}
                          </h4>
                          <div className="flex items-center gap-1 mt-1.5 text-xs text-foreground-500">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                            <span>{rv.rating}</span>
                            <span className="mx-1">·</span>
                            <span>{rv.year}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-400">暂无相关推荐</p>
                )}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* SEO Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": video.title,
        "description": video.description,
        "image": video.coverImage,
        "datePublished": `${video.year}-01-01`,
        "author": video.director ? {
          "@type": "Person",
          "name": video.director
        } : undefined,
        "publisher": {
          "@type": "Organization",
          "name": "麻花视频",
          "url": "https://www.mahuavideo.com/"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.mahuavideo.com/article/${video.id}`
        }
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.mahuavideo.com/" },
          { "@type": "ListItem", "position": 2, "name": categoryName, "item": `https://www.mahuavideo.com/category/${video.category}` },
          { "@type": "ListItem", "position": 3, "name": video.title }
        ]
      }) }} />
    </>
  );
}